import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

import { RequestError } from './RequestError';
import { StepChangedNotice } from './StepChangedNotice';
import { REASON_MAX_LENGTH, useRampCutoverMutation } from './useCutover';

import type { CutoverStepLabel } from './cutover.types';

export function NextStepDialog({
  target,
  blockers,
  hasStepChanged,
  onClose,
}: {
  target: CutoverStepLabel;
  // Why the step needs an override now; empty when it needs none
  blockers: string[];
  hasStepChanged: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const ramp = useRampCutoverMutation();
  const [overrideReason, setOverrideReason] = useState('');
  const needsOverride = blockers.length > 0;
  const trimmedReason = overrideReason.trim();
  const canSubmit =
    !ramp.isPending &&
    !hasStepChanged &&
    (!needsOverride || trimmedReason !== '');

  const submit = () =>
    ramp.mutate(
      { to: target, overrideReason: needsOverride ? trimmedReason : null },
      {
        onSuccess: (result) => {
          if (!result.accepted) return;
          toast.success(`The move to ${target} is queued`);
          onClose();
        },
      }
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move to {target}?</DialogTitle>
          <DialogDescription>
            {target === '100%'
              ? 'This moves every user to the TypeScript backend, users with a BAA included.'
              : `This moves users to the TypeScript backend until ${target} of the users without a BAA are on it. Teams move together.`}
          </DialogDescription>
        </DialogHeader>
        {needsOverride && (
          <div className="space-y-2 text-sm">
            <p className="font-medium">This step is not ready:</p>
            <ul className="list-disc space-y-1 pl-5">
              {blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
            <label className="block space-y-2 font-medium">
              <span>Override reason</span>
              <Textarea
                value={overrideReason}
                maxLength={REASON_MAX_LENGTH}
                onChange={(event) => setOverrideReason(event.target.value)}
                placeholder="Why the step should start anyway"
              />
              <span className="block font-normal text-muted-foreground">
                You can start the step anyway with a written reason. The audit
                log records it.
              </span>
            </label>
          </div>
        )}
        {hasStepChanged && <StepChangedNotice />}
        <RequestError result={ramp.data} error={ramp.error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant={needsOverride ? 'destructive' : 'default'}
            disabled={!canSubmit}
            onClick={submit}
          >
            {ramp.isPending
              ? 'Starting...'
              : needsOverride
                ? `Override and move to ${target}`
                : `Move to ${target}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
